import {
  tableV2NestedBodyRowHeightPx,
  tableV2NestedHeaderHeight,
} from '@/features/shared/components/tableSystem/TableV2Constants';
import { r, s, typographyTokens } from '@/theme/tokens';
import { MAX_PAGE_SIZE, type QueryResult } from '@flowqa/shared';
import type { SxProps, Theme } from '@/ui/primitives';

export type TableV2NestedTableDepth = 1 | 2;

export const tableV2NestedPrimaryBackground = 'var(--surface-page)';
export const tableV2NestedSecondaryBackground = 'var(--surface-page-alt)';
export const tableV2NestedBackgroundByDepth = {
  1: tableV2NestedPrimaryBackground,
  2: tableV2NestedSecondaryBackground,
} satisfies Record<TableV2NestedTableDepth, string>;

export const resolveTableV2NestedBackground = (depth: TableV2NestedTableDepth): string =>
  tableV2NestedBackgroundByDepth[depth];

type TableV2NestedQueryInput = {
  page: number;
  pageSize: number;
};

export const queryAllTableV2NestedRows = async <TRow, TInput extends TableV2NestedQueryInput>(
  input: TInput,
  queryPage: (input: TInput) => Promise<QueryResult<TRow>>,
): Promise<QueryResult<TRow>> => {
  const firstPageInput = { ...input, page: 1, pageSize: MAX_PAGE_SIZE };
  const firstPage = await queryPage(firstPageInput);
  const pageCount = Math.max(1, Math.ceil(firstPage.total / MAX_PAGE_SIZE));

  if (pageCount === 1) return firstPage;

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, async (_item, index) =>
      queryPage({ ...input, page: index + 2, pageSize: MAX_PAGE_SIZE }),
    ),
  );

  return {
    ...firstPage,
    items: [...firstPage.items, ...remainingPages.flatMap((remainingPage) => remainingPage.items)],
    page: 1,
    pageSize: MAX_PAGE_SIZE,
  };
};

// Depth 1 nested tables use the page surface. Depth 2 nested tables use the alternate page surface
// so each expansion level keeps a visible background step.
export const tableV2NestedTableFrameSx = {
  backgroundColor: tableV2NestedPrimaryBackground,
  border: (theme) => `1px solid ${theme.app.colors.border}`,
  borderRadius: r('md'),
  overflow: 'hidden',
} satisfies SxProps<Theme>;

export const tableV2NestedAltTableFrameSx = {
  backgroundColor: tableV2NestedSecondaryBackground,
  border: (theme) => `1px solid ${theme.app.colors.border}`,
  borderRadius: r('md'),
  overflow: 'hidden',
} satisfies SxProps<Theme>;

export const tableV2NestedTableSx = {
  backgroundColor: tableV2NestedPrimaryBackground,
  border: 0,
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  '&& thead th': {
    backgroundColor: tableV2NestedPrimaryBackground,
  },
  '& th': {
    backgroundColor: tableV2NestedPrimaryBackground,
    borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
    color: 'text.primary',
    fontSize: '16px',
    fontWeight: typographyTokens.body.fontWeight,
    height: tableV2NestedHeaderHeight,
    py: s('none'),
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '& td': {
    backgroundColor: tableV2NestedPrimaryBackground,
    fontSize: typographyTokens.body.fontSize,
    height: `${String(tableV2NestedBodyRowHeightPx)}px`,
  },
  '& .MuiTypography-root': {
    fontSize: typographyTokens.body.fontSize,
  },
  '& tbody tr:last-of-type > td': {
    borderBottom: 0,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedAltTableSx = {
  backgroundColor: tableV2NestedSecondaryBackground,
  border: 0,
  borderCollapse: 'separate',
  borderSpacing: 0,
  tableLayout: 'fixed',
  '&& thead th': {
    backgroundColor: tableV2NestedSecondaryBackground,
  },
  '& th': {
    backgroundColor: tableV2NestedSecondaryBackground,
    borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
    color: 'text.primary',
    fontSize: '16px',
    fontWeight: typographyTokens.body.fontWeight,
    height: tableV2NestedHeaderHeight,
    py: s('none'),
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '& td': {
    backgroundColor: tableV2NestedSecondaryBackground,
    fontSize: typographyTokens.body.fontSize,
    height: `${String(tableV2NestedBodyRowHeightPx)}px`,
  },
  '& .MuiTypography-root': {
    fontSize: typographyTokens.body.fontSize,
  },
  '& tbody tr:last-of-type > td': {
    borderBottom: 0,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedContentSx = {
  display: 'grid',
  pb: s('2xl'),
  px: s('2xl'),
} satisfies SxProps<Theme>;

export const tableV2NestedCarrierRowSx = {
  backgroundColor: 'background.paper',
  height: '0 !important',
  maxHeight: 'none !important',
  '& > td': {
    borderBottom: 0,
    height: '0 !important',
    lineHeight: 0,
    maxHeight: 'none !important',
    overflow: 'hidden',
    p: s('none'),
    verticalAlign: 'top',
  },
} satisfies SxProps<Theme>;

export const tableV2NestedBodyRowSx = {
  backgroundColor: tableV2NestedPrimaryBackground,
  '& > td': {
    backgroundColor: tableV2NestedPrimaryBackground,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedBodyAltRowSx = {
  backgroundColor: tableV2NestedSecondaryBackground,
  '& > td': {
    backgroundColor: tableV2NestedSecondaryBackground,
  },
} satisfies SxProps<Theme>;

export const tableV2NestedDepthSx = {
  1: {
    bodyRow: tableV2NestedBodyRowSx,
    frame: tableV2NestedTableFrameSx,
    table: tableV2NestedTableSx,
  },
  2: {
    bodyRow: tableV2NestedBodyAltRowSx,
    frame: tableV2NestedAltTableFrameSx,
    table: tableV2NestedAltTableSx,
  },
} satisfies Record<
  TableV2NestedTableDepth,
  {
    bodyRow: SxProps<Theme>;
    frame: SxProps<Theme>;
    table: SxProps<Theme>;
  }
>;

export const tableV2NestedDetailCellSx = {
  backgroundColor: 'background.paper',
  borderBottom: 0,
  lineHeight: 1,
  p: s('md'),
} satisfies SxProps<Theme>;
